import React from "react";
import { ExpandableFormSection, Summary } from "components/form/Sections";
import { InputSummary } from "components/StepPackageEditor/Summary/InputSummary";
import { SummaryNode } from "components/form";
import { RenderedComponentAndSummary } from "components/StepPackageEditor/EditStepPackageInputs";
import { getErrorPath } from "components/StepPackageEditor/Inputs/getErrorPath";
import { getErrorPathForPackageReference } from "components/StepPackageEditor/Inputs/Components/PackageSelector/PackageSelector";
import { exhaustiveCheck } from "utils/exhaustiveCheck";
import { CommonInputProps, StepInputComponent } from "@octopusdeploy/step-ui";
import { getPathToArrayInput, getPathToInput, getPathToInputObject } from "@octopusdeploy/runtime-inputs";

export interface InputComponentProps<StepInputs, TInputComponents> {
    content: TInputComponents;
    isExpandedByDefault: boolean;
    getRenderedComponentAndSummary(component: TInputComponents): RenderedComponentAndSummary;
}

export function Input<StepInputs, TInputComponents extends CommonInputProps>(props: InputComponentProps<StepInputs, TInputComponents>) {
    const { isExpandedByDefault } = props;
    const { label, helpText } = props.content;
    const { renderedComponent, summary } = props.getRenderedComponentAndSummary(props.content);
    return (
        <ExpandableFormSection title={label} isExpandedByDefault={isExpandedByDefault} summary={getSummary(summary)} errorKey={"todo-step-ui" + label} help={helpText}>
            {renderedComponent}
        </ExpandableFormSection>
    );
}

function getSummary(summary: InputSummary): SummaryNode {
    if (summary === "empty" || summary.value === "") {
        return Summary.placeholder("Not provided");
    }
    if (summary.isDefaultValue) {
        return Summary.default(summary.value);
    }
    return Summary.summary(summary.value);
}

export function getInputContentReactKey<TInputComponents extends CommonInputProps>(content: TInputComponents) {
    return `${content.label}`;
}

function getPossibleInputPaths(content: StepInputComponent) {
    switch (content.type) {
        case "sensitive":
            return [getPathToInput(content.input)];
        case "package":
            return getErrorPathForPackageReference(content);
        case "text":
            return [getPathToInput(content.input)];
        case "radio-buttons":
            return [getPathToInputObject(content.input)];
        case "list":
            return [getPathToArrayInput(content.input)];
        default:
            return exhaustiveCheck(content, `Section content type not yet implemented`);
    }
}

function getInputErrorKey(content: StepInputComponent): string {
    //todo: Fix up how we deal with errors, most likely as part of data loading. Ultimately, we want to deal
    //with an object of scoped errors instead of these magic strings, that way we never needed tp do any of this
    //nor did we need to magically reference inputs.
    return getPossibleInputPaths(content)
        .map((path) => `inputs${getErrorPath(path)}`)
        .join("|");
}
